/* Tenzen - desktop nav contrast over the home banner.
 *
 * The desktop deck is white glass everywhere on the site. Over the home
 * banner that is wrong: a frosted white pill sitting on full-bleed
 * photography competes with the photograph. So on the banner the surface
 * comes off entirely (see `html.tz-nav-hero` in snippets/tenzen-topnav.liquid)
 * and the mark and icons are painted straight onto the image - black or
 * white, whichever the image behind THAT pill can carry.
 *
 * Left and right are measured independently. A slide can easily be dark on
 * one side and blown out on the other, and one global decision would be
 * wrong on one of them.
 *
 * Two things make the measurement non-obvious:
 *
 *   1. The image is `object-fit: cover; object-position: center top`, so
 *      viewport coordinates do not map to source pixels directly - the
 *      overflow is split left/right and the top edge is pinned.
 *   2. The pill is small and sits near the top edge, so the read has to be
 *      the region UNDER THAT PILL, not the banner's average. A photo that
 *      is dark on the left and blown out on the right is the normal case.
 *
 * Until 2026-09-13 the banner also drew a black top scrim between the
 * photograph and this deck, and this file composited a copy of that
 * gradient over every sample. The scrim is gone, so the compositing is
 * gone with it: the luminance now comes from the artwork alone.
 *
 * Desktop only. On m-web the deck lives at the bottom of the viewport and
 * never overlaps the banner.
 */
(function () {
  'use strict';

  var hero = document.querySelector('[data-banner-carousel]');
  var navs = [].slice.call(document.querySelectorAll('.tz-nav'));
  /* The m-web home page carries a bare centred wordmark over the same
     banner. It is the phone's counterpart to the desktop deck, so it gets
     measured the same way; only one of the two is ever on screen. */
  var marks = [].slice.call(document.querySelectorAll('[data-tz-homemark]'));
  if (!hero || (!navs.length && !marks.length)) return;

  var root = document.documentElement;
  var desktop = window.matchMedia('(min-width: 990px)');

  /* Above this the backdrop carries black ink, below it white. 140 rather
     than the midpoint: white type holds on a mid-grey far better than black
     does, so the tie goes to white. */
  var INK_THRESHOLD = 140;

  /* 8x8 is enough. The question is "is this area light or dark", not what
     is in it, and averaging 64 samples of a downscaled draw is already a
     box filter over the whole region. */
  var GRID = 8;
  var canvas = document.createElement('canvas');
  canvas.width = canvas.height = GRID;
  var ctx = canvas.getContext('2d', { willReadFrequently: true });

  /* ── Sampling proxies ────────────────────────────────────────────────
     The <img> in the document cannot be drawn to a canvas: it was fetched
     without CORS, so it taints and getImageData throws. Re-request it with
     `crossOrigin` (Shopify's CDN answers `Access-Control-Allow-Origin: *`)
     and at 240px wide, which is both plenty for a luminance read and small
     enough that the extra request is noise. */
  var proxies = Object.create(null);

  function proxyFor(src) {
    if (!src) return null;
    var small = /[?&]width=\d+/.test(src)
      ? src.replace(/([?&]width=)\d+/, '$1240')
      : src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=240';
    var p = proxies[small];
    if (p) return p;
    p = new Image();
    p.crossOrigin = 'anonymous';
    p.decoding = 'async';
    p.addEventListener('load', function () { p.tzReady = true; schedule(); });
    p.addEventListener('error', function () { p.tzFailed = true; });
    p.src = small;
    proxies[small] = p;
    return p;
  }

  /* Which slide is under this pill. The track is a horizontal scroll-snap
     carousel, so "the active slide" is whichever one the pill's centre
     currently sits inside - no need to know the carousel's own index. */
  function slideUnder(rect) {
    var cx = rect.left + rect.width / 2;
    var imgs = hero.querySelectorAll('[data-bc-slide] img');
    for (var i = 0; i < imgs.length; i++) {
      var b = imgs[i].getBoundingClientRect();
      if (b.width && cx >= b.left && cx <= b.right) return imgs[i];
    }
    return null;
  }

  /* Returns 'light' (paint the deck white) or 'dark' (paint it black), or
     null when the measurement is not available yet. */
  function toneFor(rect) {
    var img = slideUnder(rect);
    if (!img) return null;

    var proxy = proxyFor(img.currentSrc || img.src);
    if (!proxy || !proxy.tzReady || !proxy.naturalWidth) return null;

    var box = img.getBoundingClientRect();
    var nw = img.naturalWidth, nh = img.naturalHeight;
    if (!nw || !nh || !box.width) return null;

    /* Undo `object-fit: cover; object-position: center top`. */
    var scale = Math.max(box.width / nw, box.height / nh);
    var originX = box.left + (box.width - nw * scale) / 2;
    var originY = box.top;
    /* Full-size source px -> proxy px. */
    var k = proxy.naturalWidth / nw;

    var sx = ((rect.left - originX) / scale) * k;
    var sy = ((rect.top - originY) / scale) * k;
    var sw = (rect.width / scale) * k;
    var sh = (rect.height / scale) * k;

    /* A pill can overhang the drawn image on an extreme viewport; clamp
       rather than let drawImage throw or read outside the bitmap. */
    sx = Math.max(0, Math.min(sx, proxy.naturalWidth - 1));
    sy = Math.max(0, Math.min(sy, proxy.naturalHeight - 1));
    sw = Math.max(1, Math.min(sw, proxy.naturalWidth - sx));
    sh = Math.max(1, Math.min(sh, proxy.naturalHeight - sy));

    var data;
    try {
      ctx.clearRect(0, 0, GRID, GRID);
      ctx.drawImage(proxy, sx, sy, sw, sh, 0, 0, GRID, GRID);
      data = ctx.getImageData(0, 0, GRID, GRID).data;
    } catch (e) {
      return null;   /* tainted canvas - fall back to the site default */
    }

    var lum = 0;
    for (var i = 0; i < data.length; i += 4) {
      lum += 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
    }
    lum /= (data.length / 4);

    return lum > INK_THRESHOLD ? 'dark' : 'light';
  }

  function unpaint(nav) {
    nav.style.removeProperty('--tz-ink');
    nav.style.removeProperty('--tz-sel');
    nav.style.removeProperty('--tz-mark-ink');
  }

  function apply() {
    /* Desktop measures the deck, a phone measures the home wordmark. The
       other one is display:none at that width, so measuring it would read a
       zero-sized rect and paint nothing useful. */
    var onDesktop = desktop.matches;
    var targets = onDesktop ? navs : marks;
    if (!onDesktop) {
      /* `tz-nav-hero` strips the desktop pill's glass; it means nothing at
         this width and the deck is hidden, so clear both. */
      root.classList.remove('tz-nav-hero');
      navs.forEach(unpaint);
    }
    if (!targets.length) return;

    var heroRect = hero.getBoundingClientRect();
    var onBanner = false;

    targets.forEach(function (nav) {
      var rect = nav.getBoundingClientRect();
      /* "On the banner" means fully inside it. A pill straddling the bottom
         edge has no single correct answer, so it waits until it is clear. */
      if (rect.top < heroRect.top || rect.bottom > heroRect.bottom) {
        unpaint(nav);
        return;
      }
      onBanner = true;

      /* Default 'dark' when the read fails: the deck's own scheme is black
         ink, so an unmeasurable banner just looks like the rest of the site
         with the glass off, rather than like a bug. */
      var tone = toneFor(rect) || 'dark';
      var light = tone === 'light';
      nav.style.setProperty('--tz-ink', light ? '#fff' : '#000');
      /* The bare wordmark reads this one; see the note on `.tz-homemark`
         in snippets/tenzen-topnav.liquid for why it is not `--tz-ink`. */
      nav.style.setProperty('--tz-mark-ink', light ? '#fff' : '#000');
      /* The hover chip re-derives from the same tone. Left on its light-mode
         value it would be a black chip on a dark photograph. */
      nav.style.setProperty('--tz-sel', light ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.08)');
    });

    if (onDesktop) root.classList.toggle('tz-nav-hero', onBanner);
  }

  var frame = 0;
  function schedule() {
    if (frame) return;
    frame = requestAnimationFrame(function () { frame = 0; apply(); });
  }

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  /* bfcache restores do not re-run the module, and the deck's state is
     scroll-dependent, so re-measure on the way back in. */
  window.addEventListener('pageshow', schedule);

  var track = hero.querySelector('[data-bc-track]');
  if (track) track.addEventListener('scroll', schedule, { passive: true });

  if (desktop.addEventListener) desktop.addEventListener('change', schedule);
  else if (desktop.addListener) desktop.addListener(schedule);

  /* A scroll changes what is behind a FIXED mark, so the phone needs the
     same re-measure the desktop deck already gets from the scroll listener
     above. Nothing extra to bind: that listener is not desktop-gated. */

  /* Slides arrive after this runs; measure again as each one lands. */
  [].forEach.call(hero.querySelectorAll('[data-bc-slide] img'), function (img) {
    if (img.complete) schedule();
    else img.addEventListener('load', schedule, { once: true });
  });

  schedule();
}());
